import React, { useEffect, useState } from "react";
import { Container, Table, Spinner, Card, Button, Form } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { Utils } from "../../common/Utils";
import { useGlobal } from "../../contexts/GlobalContext";
import { Order } from "../../model/OrderModel";
import { OrderGateway } from "../../gateways/OrderGateway";

const OrderDetails: React.FC = () => {
  const { orderId, role } = useParams(); // Get the order ID from URL
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<string>("");
  const { settings, user } = useGlobal();
  const navigate = useNavigate();
  const [updateLoading, setUpdateLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchOrder = async () => {

      if (!user) {
        console.log("No user is signed in.");
        setLoading(false);
        return;
      }

      const theOrder = await OrderGateway.GetOrder(orderId as string);
      if (theOrder) {
        setOrder(theOrder);
        setStatus(theOrder.status);
      }

      setLoading(false);
    };

    fetchOrder();
  }, [orderId, user]);

  const handleStatusUpdate = async () => {
    if (!orderId || !status) return;

    setUpdateLoading(true);
    setTimeout(async () => {
      OrderGateway.UpdateOrder(orderId, status);
      setUpdateLoading(false);
    }, 1000);
  };


  return (
    <Container className="main-container mt-4">
      <h2>Order Details</h2>

      {loading || !settings ? (
        <Spinner animation="border" />
      ) : !order ? (
        <p className="text-center">Order not found.</p>
      ) : (
        <>
          <Card className="p-3 mb-3 shadow-sm">
            <h4>Order ID: {order.orderNumber}</h4>
            <p>Placed on: {new Date(order.createdAt).toLocaleString()}</p>
            <p>Delivery Location: {order.deliveryLocation || "Collection"}</p>
            {role === "admin" &&
              (<>
                <p>Customer: {`${order.firstName} ${order.lastName}`}</p>
                <p>Email: {order.email}</p>
                {order.phoneNumber && <p>Phone Number: {order.phoneNumber}</p>}
              </>)}
            {order.comments && <p>Comments:<br /><span style={{ fontStyle: "italic" }}>{order.comments}</span></p>}

            {/* Show status update dropdown if the role includes "admin" */}
            {role === "admin" ? (
              <Form.Group className="mt-3 d-flex align-items-center gap-2">
                <Form.Label className="mb-0">Status:</Form.Label>

                <Form.Select
                  className="w-auto"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="New">New</option>
                  <option value="In-progress">In-progress</option>
                  <option value="Completed">Completed</option>
                  <option value="Cancelled">Cancelled</option>
                </Form.Select>
                <Button
                  variant="primary"
                  onClick={handleStatusUpdate}
                  disabled={updateLoading}
                >
                  {updateLoading ? <Spinner animation="border" size="sm" /> : "Update"}
                </Button>
              </Form.Group>
            ) :
              (<p>
                Status:{" "}
                <span
                  className={`badge ${order.status === "Completed"
                    ? "bg-success"
                    : order.status === "Cancelled" 
                    ? "bg-danger" 
                    : "bg-warning text-dark"
                    }`}
                >
                  {order.status}
                </span>
              </p>)}

          </Card>
          <h4>Order Summary</h4>
          <Table striped bordered hover responsive>
            <thead className="table-dark">
              <tr>
                <th>Product</th>
                <th className="text-end">Quantity</th>
                {(settings.hasPrice || role === "admin") && <th className="text-end">Total</th>}
              </tr>
            </thead>
            <tbody>
              {order.cartItems.map(item => (
                <tr key={item.id}>
                  <td>
                    <div className="d-flex align-items-center">
                      {item.imageUrl && <img src={item.imageUrl} alt={item.name} width="50" height="50" className="me-2" />}
                      <div>
                        <strong>{item.name}</strong>
                        <br />
                        <small className="text-muted">
                          {Object.entries(item.attributes).map(([key, value]) => `${key}: ${value}`).join(", ")}
                        </small>
                      </div>
                    </div>
                  </td>
                  <td className="text-end">{item.quantity}</td>
                  {(settings.hasPrice || role === "admin") && <td className="text-end">{Utils.FormatCurrency(Number(item.priceAfterTax)* item.quantity, settings.currency)}</td>}
                </tr>
              ))}
            </tbody>
          </Table>
          {(settings.hasPrice || role === "admin") &&
            <div className="text-end">
              <p>Subtotal: <strong>{Utils.FormatCurrency(Number(order.subtotal), settings.currency)}</strong></p>              
              <p>Delivery Fee: {Utils.FormatCurrency(Number(order.deliveryFee), settings.currency)}</p>
              <p>VAT ({(100 * (Number(order.totalAmount) / (Number(order.totalAmount) - Number(order.vatAmount)) - 1)).toFixed(0)}%): <strong>{Utils.FormatCurrency(Number(order.vatAmount), settings.currency)}</strong></p>
              <h5>Total: <strong>{Utils.FormatCurrency(Number(order.totalAmount), settings.currency)}</strong></h5>
            </div>
          }
          <hr />

          <div className="text-end mt-3">
            <button className="btn btn-primary" onClick={() => navigate(Utils.GetUrl("/orders"))}>Back to Orders</button>
          </div>
        </>
      )}
    </Container>
  );
};

export default OrderDetails;